import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  CircularProgress,
  Chip,
  Divider
} from '@mui/material';
import PsychologyIcon from '@mui/icons-material/Psychology';

const AIAnalysisModal = ({ open, onClose, aiData, parameterName }) => {
  const [showNoData, setShowNoData] = React.useState(false);
  
  // Show "no data" message after 10 seconds if no AI data is received
  React.useEffect(() => {
    if (open && !aiData) {
      const timer = setTimeout(() => {
        setShowNoData(true);
      }, 10000);
      
      return () => clearTimeout(timer);
    } else {
      setShowNoData(false);
    }
  }, [open, aiData]);
  
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '12px',
          boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
        }
      }}
    >
      <DialogTitle sx={{ 
        background: 'linear-gradient(135deg, #2AAB6C 0%, #097EB7 100%)',
        color: 'white',
        display: 'flex', 
        alignItems: 'center', 
        gap: 1.5,
        py: 2.5,
        fontSize: '1.25rem',
        fontWeight: 600
      }}>
        <PsychologyIcon sx={{ fontSize: '1.5rem' }} />
        AI Health Analysis - {parameterName}
      </DialogTitle>
      
      <DialogContent sx={{ p: 0 }}>
        {!aiData ? (
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center', 
            justifyContent: 'center',
            py: 6,
            px: 3
          }}>
            {!showNoData ? (
              <>
                <CircularProgress 
                  sx={{ 
                    color: '#2AAB6C', 
                    mb: 3,
                    '& .MuiCircularProgress-circle': {
                      strokeLinecap: 'round'
                    }
                  }} 
                  size={48}
                  thickness={4}
                />
                <Typography variant="h6" sx={{ color: '#333', mb: 1, fontWeight: 500 }}>
                  Analyzing Health Data
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Please wait while our AI processes health information...
                </Typography>
              </>
            ) : (
              <>
                <Box sx={{ 
                  width: 64, 
                  height: 64, 
                  borderRadius: '50%', 
                  backgroundColor: '#f5f5f5', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  mb: 2
                }}>
                  <PsychologyIcon sx={{ fontSize: '2rem', color: '#999' }} />
                </Box>
                <Typography variant="h6" sx={{ color: '#666', mb: 1, fontWeight: 500 }}>
                  Analysis Unavailable
                </Typography>
                <Typography variant="body2" color="text.secondary" textAlign="center">
                  The AI analysis could not be completed at this time. Please try again later.
                </Typography>
              </>
            )}
          </Box>
        ) : (
          <Box>
            {/* Header Section */}
            <Box sx={{ 
              background: 'linear-gradient(135deg, rgba(42, 171, 108, 0.1) 0%, rgba(9, 126, 183, 0.1) 100%)',
              p: 3,
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center'
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography variant="h6" sx={{ color: '#333', fontWeight: 600 }}>
                  Risk Assessment:
                </Typography>
                <Chip
                  label={aiData.risk_level?.toUpperCase()}
                  sx={{
                    backgroundColor: aiData.risk_level === 'low' ? '#E8F5E8' : 
                                   aiData.risk_level === 'moderate' ? '#FFF4E6' : '#FFEBEE',
                    color: aiData.risk_level === 'low' ? '#2AAB6C' : 
                           aiData.risk_level === 'moderate' ? '#FF9800' : '#F44336',
                    fontWeight: 700,
                    fontSize: '0.875rem',
                    height: 32
                  }}
                />
              </Box>
              
              {aiData.dateRange && (
                <Typography variant="body2" sx={{ 
                  color: '#666',
                  fontStyle: 'italic',
                  backgroundColor: 'rgba(255,255,255,0.8)',
                  px: 2,
                  py: 0.5,
                  borderRadius: '16px',
                  fontSize: '0.875rem'
                }}>
                  {aiData.dateRange}
                </Typography>
              )}
            </Box>
            
            {/* Content Section */}
            <Box sx={{ p: 3 }}>
              {/* Analysis */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" sx={{ 
                  color: '#2AAB6C', 
                  mb: 1.5, 
                  fontWeight: 600,
                  fontSize: '1.1rem'
                }}>
                  📊 Analysis
                </Typography>
                <Typography variant="body1" sx={{ 
                  color: '#444', 
                  lineHeight: 1.6,
                  backgroundColor: '#f8f9fa',
                  p: 2,
                  borderRadius: '8px',
                  borderLeft: '4px solid #2AAB6C'
                }}>
                  {aiData.explanation}
                </Typography>
              </Box>
              
              <Divider sx={{ my: 2 }} />
              
              {/* Trends */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" sx={{ 
                  color: '#097EB7', 
                  mb: 1.5, 
                  fontWeight: 600,
                  fontSize: '1.1rem'
                }}>
                  📈 Trends & Monitoring
                </Typography>
                <Typography variant="body1" sx={{ 
                  color: '#444', 
                  lineHeight: 1.6,
                  backgroundColor: '#f0f8ff',
                  p: 2,
                  borderRadius: '8px',
                  borderLeft: '4px solid #097EB7'
                }}>
                  {aiData.trends}
                </Typography>
              </Box>
              
              {/* Recommendations */}
              {aiData.advice && Array.isArray(aiData.advice) && aiData.advice.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" sx={{ 
                    color: '#FF9800', 
                    mb: 1.5, 
                    fontWeight: 600,
                    fontSize: '1.1rem'
                  }}>
                    💡 Recommendations
                  </Typography>
                  <Box sx={{ 
                    backgroundColor: '#fff8e1',
                    p: 2,
                    borderRadius: '8px',
                    borderLeft: '4px solid #FF9800'
                  }}>
                    {aiData.advice.map((advice, index) => (
                      <Box key={index} sx={{ 
                        display: 'flex', 
                        alignItems: 'flex-start', 
                        mb: index < aiData.advice.length - 1 ? 1.5 : 0,
                        gap: 1
                      }}>
                        <Box sx={{ 
                          width: 6, 
                          height: 6, 
                          borderRadius: '50%', 
                          backgroundColor: '#FF9800',
                          mt: 0.75,
                          flexShrink: 0
                        }} />
                        <Typography variant="body2" sx={{ color: '#444', lineHeight: 1.5 }}>
                          {advice}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}
              
              {/* Doctor Consultation */}
              {aiData.doctor_consultation && (
                <Box>
                  <Typography variant="h6" sx={{ 
                    color: '#9C27B0', 
                    mb: 1.5, 
                    fontWeight: 600,
                    fontSize: '1.1rem'
                  }}>
                    🩺 Doctor Consultation
                  </Typography>
                  <Box sx={{ 
                    backgroundColor: '#f3e5f5',
                    p: 2.5,
                    borderRadius: '8px',
                    borderLeft: '4px solid #9C27B0'
                  }}>
                    <Typography variant="body1" sx={{ color: '#444', lineHeight: 1.6 }}>
                      {aiData.doctor_consultation}
                    </Typography>
                  </Box>
                </Box>
              )}
            </Box>
          </Box>
        )}
      </DialogContent>
      
      <DialogActions sx={{ 
        p: 3, 
        backgroundColor: '#f8f9fa',
        borderTop: '1px solid #e0e0e0'
      }}>
        <Button
          onClick={onClose}
          variant="contained"
          sx={{
            background: 'linear-gradient(135deg, #2AAB6C 0%, #097EB7 100%)',
            color: 'white',
            px: 4,
            py: 1,
            borderRadius: '8px',
            fontWeight: 600,
            textTransform: 'none',
            fontSize: '1rem',
            boxShadow: '0 4px 12px rgba(42, 171, 108, 0.3)',
            '&:hover': {
              background: 'linear-gradient(135deg, #238B5B 0%, #0866A3 100%)',
              boxShadow: '0 6px 16px rgba(42, 171, 108, 0.4)'
            }
          }}
        >
          Close Analysis
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AIAnalysisModal;